import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DirectionsIcon from '@mui/icons-material/Directions';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../hooks/useUserProfile';
import { quikqAuthService } from '../services/quikqAuth';
import { logger, LogCategory } from '../services/logger';

const helpOptions = [
  { value: 'app', label: 'App Technical Support' },
];
const priorities = [
  { value: 'low', label: 'Low - General inquiry' },
  { value: 'medium', label: 'Medium - Need assistance' },
  { value: 'high', label: 'High - Urgent' },
];
const faqs = [
  {
    q: 'How do I navigate to a fuel station?',
    a: 'When you click on a fuel station on the map and select "Get Directions", the app will calculate routes and display them. You can then click "Open in Google Maps" to use Google Maps for turn-by-turn navigation to the station.'
  },
  {
    q: 'How do I add drivers to my account?',
    a: 'Go to Profile > Drivers to add and manage drivers for your account. You can assign fuel cards to specific drivers from this section.'
  },
  {
    q: 'Where are the settings for notifications and timeout?',
    a: 'Go to Profile > Settings to configure notification preferences and session timeout settings. You can customize how and when you receive notifications, and set how long your session stays active.'
  },
  {
    q: 'How do I add a fuel card to my account?',
    a: 'Go to Profile > Wallet > Manage Cards and follow the instructions to add your fuel card.'
  },
  {
    q: "+ Why aren't fuel stations showing up on the map?",
    a: 'Make sure location permissions are enabled and you have an active internet connection.'
  },
  {
    q: 'How are fuel savings calculated?',
    a: 'Savings are calculated based on the difference between the regular price and your discounted price at each station.'
  },
  {
    q: 'Can I use the app offline?',
    a: 'Some features require internet access, but you can view previously loaded data offline.'
  },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Support contact information
const SUPPORT_PHONE = '18007847577'; // 1-800-QUIKQ (1-800-784-7577) - update with actual number if different
const SUPPORT_EMAIL = 'customer@quikq.com';

const Support: React.FC = () => {
  const { userProfile, loading: profileLoading, error: profileError } = useUserProfile();
  const [form, setForm] = useState({
    email: '',
    startCode: '',
    topic: 'app',
    priority: 'low',
    subject: '',
    message: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  // Update form with user profile data when available
  useEffect(() => {
    if (userProfile) {
      setForm(prev => ({
        ...prev,
        email: userProfile.email || '',
        startCode: userProfile.startCode || ''
      }));
    }
  }, [userProfile]);

  // Log when Support page is accessed
  useEffect(() => {
    if (userProfile) {
      logger.info(LogCategory.NAVIGATION, 'Support page accessed', {
        userId: userProfile.userId,
        startCode: userProfile.startCode
      });
    }
  }, [userProfile]); // Log when user profile is available

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAccordionChange = (section: string) => {
    const isExpanding = expanded !== section;
    setExpanded(isExpanding ? section : false);
    logger.logUserAction(isExpanding ? 'Support: Tutorial section opened' : 'Support: Tutorial section closed', {
      section: section,
      userId: userProfile?.userId,
      startCode: userProfile?.startCode
    });
  };

  const handleFAQChange = (index: number, question: string) => {
    const isExpanding = expanded !== String(index);
    setExpanded(isExpanding ? String(index) : false);
    logger.logUserAction(isExpanding ? 'Support: FAQ opened' : 'Support: FAQ closed', {
      faqIndex: index,
      question: question,
      userId: userProfile?.userId,
      startCode: userProfile?.startCode
    });
  };

  const handleCallSupport = () => {
    logger.logUserAction('Support: Initiated phone call to support', {
      phone: SUPPORT_PHONE,
      userId: userProfile?.userId
    });
    window.location.href = `tel:+${SUPPORT_PHONE}`;
  };

  const handleEmailSupport = () => {
    logger.logUserAction('Support: Opened email client for support', {
      email: SUPPORT_EMAIL,
      userId: userProfile?.userId
    });
    const subject = encodeURIComponent('Fuel Card Support Request');
    const body = encodeURIComponent(`Hello,\n\nI need assistance with my fuel card.\n\nThank you.`);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!form.subject.trim()) {
      setEmailError('Subject is required');
      logger.debug(LogCategory.UI, 'Support: Ticket submission validation failed - missing subject', {
        userId: userProfile?.userId,
        topic: form.topic,
        priority: form.priority
      });
      return;
    }
    
    if (!form.message.trim()) {
      setEmailError('Message is required');
      logger.debug(LogCategory.UI, 'Support: Ticket submission validation failed - missing message', {
        userId: userProfile?.userId,
        topic: form.topic,
        priority: form.priority,
        subject: form.subject
      });
      return;
    }
    
    if (!form.email.trim()) {
      setEmailError('Email address is required');
      logger.debug(LogCategory.UI, 'Support: Ticket submission validation failed - missing email', {
        userId: userProfile?.userId,
        topic: form.topic,
        priority: form.priority
      });
      return;
    }

    setEmailSending(true);
    setEmailError('');
    setEmailSuccess(false);

    try {
      // Determine email recipient based on topic
      const emailRecipient = form.topic === 'app' 
        ? 'support@nstartech.net' 
        : 'customer@quikq.com';

      // Create email data
      const emailData = {
        to: emailRecipient,
        from: form.email,
        subject: `[${form.topic.toUpperCase()}] ${form.subject}`,
        message: `
From: ${form.email}
Start Code: ${form.startCode}
Topic: ${helpOptions.find(opt => opt.value === form.topic)?.label || form.topic}
Priority: ${priorities.find(opt => opt.value === form.priority)?.label || form.priority}

Message:
${form.message}

---
Sent from NorthStar Fuel App
        `.trim(),
        userInfo: {
          email: form.email,
          startCode: form.startCode,
          topic: form.topic,
          priority: form.priority
        }
      };

      const response = await fetch('/api/support-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Server error while sending email.');
      }

      setEmailSuccess(true);
      
      // Log successful ticket submission
      logger.logUserAction('Support: Ticket submitted successfully', {
        topic: form.topic,
        priority: form.priority,
        subject: form.subject,
        emailRecipient: emailRecipient,
        userId: userProfile?.userId,
        startCode: form.startCode
      });
      
      setForm(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
      
      // Clear success message after 5 seconds
      setTimeout(() => setEmailSuccess(false), 5000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email. Please try again or contact support directly.';
      setEmailError(errorMessage);
      
      // Log failed ticket submission attempt
      logger.error(LogCategory.UI, 'Support: Ticket submission failed', error instanceof Error ? error : new Error(String(error)), {
        topic: form.topic,
        priority: form.priority,
        subject: form.subject,
        email: form.email,
        startCode: form.startCode,
        userId: userProfile?.userId,
        errorMessage: errorMessage
      });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Box sx={{ p: 2, pb: { xs: 12, sm: 12, md: 12 }, bgcolor: '#f5f6fa', minHeight: '100vh' }}>

      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          position: 'relative',
          zIndex: 1
        }}>
          <Box
            onClick={() => navigate(quikqAuthService.isDriver() ? '/map' : '/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'text.primary',
              mr: 2,
              '&:hover': { opacity: 0.7 }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20, mr: 0.5 }} />
            <Typography variant="body2" fontWeight="500">Back</Typography>
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
          FAQ / Support
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 1 }}>
          Get help with Northstar Fuel
        </Typography>
      </Box>

      {/* Need Immediate Help? */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: '#eaf3fd', border: '1px solid #b6d4fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon sx={{ color: '#1976d2', mr: 1 }} />
          <Typography fontWeight={600} color="#1976d2">Need Immediate Help?</Typography>
        </Box>
        <Typography fontSize={12} color="#666" sx={{ mb: 1.5, fontStyle: 'italic' }}>
          For fuel card support and account issues
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box>
            <Typography fontSize={14} color="#757575" fontWeight={500}>Card Support - Call</Typography>
            <Typography fontSize={12} color="#757575">24/7 emergency assistance</Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ bgcolor: '#1976d2', fontWeight: 600, minWidth: 80 }} 
            startIcon={<CallIcon />}
            onClick={handleCallSupport}
          >
            Call
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            <Typography fontSize={14} color="#757575" fontWeight={500}>Card Support - Email</Typography>
            <Typography fontSize={12} color="#757575">Response within 4 hours</Typography>
          </Box>
          <Button 
            variant="outlined" 
            size="small" 
            sx={{ fontWeight: 600, minWidth: 80 }} 
            startIcon={<EmailIcon />}
            onClick={handleEmailSupport}
          >
            Email
          </Button>
        </Box>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #b6d4fa' }}>
          <Typography fontSize={12} color="#666" fontWeight={500}>
            For app technical support, please use the "Support Ticket" tab below.
          </Typography>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => {
            setTabValue(newValue);
            const tabNames = ['FAQ', 'Support Ticket', 'Tutorial'];
            logger.logUserAction(`Support: Switched to ${tabNames[newValue]} tab`, {
              tabIndex: newValue,
              tabName: tabNames[newValue],
              userId: userProfile?.userId
            });
          }} 
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="FAQ" />
          <Tab label="Support Ticket" />
          <Tab label="Tutorial" />
        </Tabs>
      </Paper>

      {/* FAQ Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AssignmentIcon sx={{ color: '#ab47bc', mr: 1 }} />
            <Typography fontWeight={700} fontSize={16} color="#ab47bc">Frequently Asked Questions</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} expanded={expanded === String(i)} onChange={() => handleFAQChange(i, faq.q)} sx={{ boxShadow: 'none', mb: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600} fontSize={15}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={14}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </TabPanel>

      {/* Support Ticket Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <AssignmentIcon sx={{ color: '#4caf50', mr: 1 }} />
            <Typography fontWeight={700} fontSize={17} color="#4caf50">App Support Ticket</Typography>
          </Box>
          <Typography fontSize={12} color="#666" sx={{ mb: 2, fontStyle: 'italic' }}>
            For app technical support and general questions
          </Typography>
        <Paper elevation={0} sx={{ p: 1, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e0e0e0' }}>
          <Typography fontSize={14} fontWeight={600}>Your Information</Typography>
          {profileLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography fontSize={13}>Loading profile...</Typography>
            </Box>
          ) : profileError ? (
            <Typography fontSize={13} color="error">
              Error loading profile. Please enter your information manually.
            </Typography>
          ) : (
            <>
              <Typography fontSize={13}>Email: {form.email || 'Please enter your email'}</Typography>
              <Typography fontSize={13}>Start Code: {form.startCode || 'Please enter your start code'}</Typography>
            </>
          )}
        </Paper>
        
        {/* Manual input fields if profile not loaded */}
        {profileError && (
          <>
            <TextField
              label="Your Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              size="small"
              margin="dense"
              sx={{ mb: 1 }}
              placeholder="Enter your email address"
              type="email"
            />
            <TextField
              label="Your Start Code"
              name="startCode"
              value={form.startCode}
              onChange={handleChange}
              fullWidth
              size="small"
              margin="dense"
              sx={{ mb: 1 }}
              placeholder="Enter your start code"
            />
          </>
        )}
        
        <TextField
          label="What can we help you with?"
          name="topic"
          value={form.topic}
          onChange={handleChange}
          select
          fullWidth
          size="small"
          margin="dense"
          sx={{ mb: 1 }}
        >
          {helpOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Priority Level"
          name="priority"
          value={form.priority}
          onChange={handleChange}
          select
          fullWidth
          size="small"
          margin="dense"
          sx={{ mb: 1 }}
        >
          {priorities.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Subject"
          name="subject"
          value={form.subject}
          onChange={handleChange}
          fullWidth
          size="small"
          margin="dense"
          sx={{ mb: 1 }}
          placeholder="Brief description of your issue"
        />
        <TextField
          label="Message"
          name="message"
          value={form.message}
          onChange={handleChange}
          fullWidth
          size="small"
          margin="dense"
          multiline
          minRows={3}
          placeholder="Please describe your issue in detail. Include any error messages, steps you took, and what you expected to happen."
        />
        {/* Success Message */}
        {emailSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Email sent successfully! We'll get back to you within 4 hours.
          </Alert>
        )}

        {/* Error Message */}
        {emailError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {emailError}
          </Alert>
        )}

        <Button 
          variant="contained" 
          sx={{ bgcolor: '#1A5FB4', fontWeight: 600, mt: 2 }} 
          fullWidth
          onClick={handleSendEmail}
          disabled={emailSending || !form.subject.trim() || !form.message.trim()}
          startIcon={emailSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        >
          {emailSending ? 'Sending...' : 'Send Message'}
        </Button>
        </Paper>
      </TabPanel>

      {/* Tutorial Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssignmentIcon sx={{ color: '#ff9800', mr: 1 }} />
            <Typography fontWeight={700} fontSize={17} color="#ff9800">Tutorial & Guides</Typography>
          </Box>
          <Typography fontSize={14} color="#666" sx={{ mb: 3 }}>
            Learn how to use NorthStar Fuel app effectively with these step-by-step guides.
          </Typography>

          {/* Map Usage Guide - For All Users */}
          <Accordion expanded={expanded === 'map-usage'} onChange={() => handleAccordionChange('map-usage')} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize={15}>How to Use the Map</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={14} sx={{ mb: 2 }}>
                <strong>Step 1: Viewing the Map</strong><br />
                When you open the map, you'll see fuel stations marked with icons. The map automatically centers on your location (if location services are enabled) or shows a default area.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 2 }}>
                <strong>Step 2: Finding Stations</strong><br />
                • Use the search bar at the top to search for specific locations or addresses<br />
                • Click and drag the map to explore different areas<br />
                • Use the zoom controls (+ and - buttons) or pinch to zoom on mobile devices<br />
                • Click the "Locate Me" button (blue circle with crosshair icon) to center the map on your current location
              </Typography>
              <Typography fontSize={14} sx={{ mb: 2 }}>
                <strong>Step 3: Viewing Station Details</strong><br />
                Click on any fuel station marker to open a popup showing:
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>Station name and full address</li>
                  <li>Current fuel prices for different fuel types</li>
                  <li>Your discounted price (if available)</li>
                  <li>Distance from your current location</li>
                </ul>
              </Typography>
              <Typography fontSize={14} sx={{ mb: 2 }}>
                <strong>Step 4: Getting Directions</strong><br />
                • Click "Get Directions" in the station popup to calculate routes<br />
                • The app will show you multiple route options (if available)<br />
                • Click on a route option to see details (distance, estimated time)<br />
                • Click "Open in Google Maps" (external link icon) to navigate with turn-by-turn directions
              </Typography>
              <Typography fontSize={14} sx={{ mb: 2 }}>
                <strong>Step 5: Planning Multi-Stop Routes</strong><br />
                • Click the "Plan Route" button (directions icon) in the bottom right<br />
                • Enter your starting point and destination<br />
                • Add fuel stations as stops along your route by clicking "Add as Stop" on any station<br />
                • The app will calculate the optimal route with all your stops<br />
                • Click "Open in Google Maps" to navigate the complete route
              </Typography>
              <Typography fontSize={14}>
                <strong>Step 6: Location Services</strong><br />
                For the best experience, enable location permissions. This allows the app to:
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>Show your current position on the map</li>
                  <li>Calculate accurate distances to stations</li>
                  <li>Provide better route suggestions</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Map Icons Legend - For All Users */}
          <Accordion expanded={expanded === 'map-legend'} onChange={() => handleAccordionChange('map-legend')} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize={15}>Map Icons Legend</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={14} fontWeight={600} sx={{ mb: 1.5, color: '#1A5FB4' }}>
                Station Markers (on the map):
              </Typography>
              <Box sx={{ mb: 2, pl: 2 }}>
                
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>⚪ White circle with logo</strong> - Stations without Discounts.
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔵 Blue circle with NSF logo</strong> - Northstar Fuel partner stations
                </Typography>
              </Box>

              <Typography fontSize={14} fontWeight={600} sx={{ mb: 1.5, color: '#1A5FB4', mt: 2 }}>
                Control Buttons (bottom right of map):
              </Typography>
              <Box sx={{ mb: 2, pl: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: '#1A5FB4', color: 'white' }}>
                    <MyLocationIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>"Locate Me" button</strong> - Centers map on your current location
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', color: '#1A5FB4', border: '1px solid #e0e0e0' }}>
                    <AddIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>Zoom in button</strong> - Makes the map view larger
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', color: '#1A5FB4', border: '1px solid #e0e0e0' }}>
                    <RemoveIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>Zoom out button</strong> - Makes the map view smaller
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', color: '#1A5FB4', border: '1px solid #e0e0e0' }}>
                    <DirectionsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>"Plan Route" button</strong> - Opens route planning dialog
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', color: '#1A5FB4', border: '1px solid #e0e0e0' }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>"Cancel Route" button</strong> - Clears current route (appears when route is active)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: 'white', color: '#1A5FB4', border: '1px solid #e0e0e0' }}>
                    <OpenInNewIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Typography fontSize={13}>
                    <strong>"Open in Google Maps" button</strong> - Opens route in Google Maps app (appears when route is active)
                  </Typography>
                </Box>
              </Box>

              <Typography fontSize={14} fontWeight={600} sx={{ mb: 1.5, color: '#1A5FB4', mt: 2 }}>
                Route Markers (when route is active):
              </Typography>
              <Box sx={{ mb: 2, pl: 2 }}>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔵 Blue circle with pin</strong> - Start point of your route
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🟢 Green pin marker</strong> - Destination point of your route
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔴 Red circle with pin</strong> - Checkpoint/Stop along your route (fuel stations added as stops)
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔵 Blue line</strong> - Selected route path (highlighted)
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔷 Light blue line</strong> - Alternative route options (click to select)
                </Typography>
              </Box>

              <Typography fontSize={14} fontWeight={600} sx={{ mb: 1.5, color: '#1A5FB4', mt: 2 }}>
                Other Elements:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔵 Blue pulsing dot</strong> - Your current location (if location services enabled)
                </Typography>
                <Typography fontSize={13} sx={{ mb: 0.5 }}>
                  <strong>🔍 Search icon (top)</strong> - Search bar for finding locations or addresses
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Getting Started Section - For All Users */}
          <Accordion expanded={expanded === 'getting-started'} onChange={() => handleAccordionChange('getting-started')} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize={15}>Getting Started</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>Step 1: Login</strong><br />
                Enter your credentials to access your NorthStar Fuel account. If you're a driver, you'll be taken directly to the map view.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>Step 2: Explore the Map</strong><br />
                The map shows all available fuel stations in your area. Click on any station to see details, prices, and savings.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>Step 3: Get Directions</strong><br />
                Select a fuel station and click "Get Directions" to see route options. You can then open the route in Google Maps for turn-by-turn navigation.
              </Typography>
              {!quikqAuthService.isDriver() && (
                <Typography fontSize={14}>
                  <strong>Step 4: Track Your Savings</strong><br />
                  View your fuel savings on the Savings tab to see how much you're saving with NorthStar Fuel.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Managing Your Account Section - Only for Regular Users */}
          {!quikqAuthService.isDriver() && (
            <Accordion expanded={expanded === 'managing-account'} onChange={() => handleAccordionChange('managing-account')} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600} fontSize={15}>Managing Your Account</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>Profile Settings</strong><br />
                  Access your profile to view account details, update information, and manage preferences.
                </Typography>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>Fuel Cards</strong><br />
                  Add and manage your fuel cards in the Profile section. You can assign cards to specific drivers if you have multiple drivers on your account.
                </Typography>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>Drivers Management</strong><br />
                  If you're an account administrator, you can add and manage drivers, assign fuel cards, and track driver activity.
                </Typography>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>Notifications</strong><br />
                  Configure notification preferences in Settings to stay updated on important account activities and fuel price changes.
                </Typography>
                <Typography fontSize={14}>
                  <strong>Session Timeout</strong><br />
                  Adjust your session timeout settings to control how long you stay logged in. This helps balance security and convenience.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Understanding Savings Section - Only for Regular Users */}
          {!quikqAuthService.isDriver() && (
            <Accordion expanded={expanded === 'understanding-savings'} onChange={() => handleAccordionChange('understanding-savings')} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600} fontSize={15}>Understanding Your Savings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>How Savings Are Calculated</strong><br />
                  Savings are calculated as the difference between the regular retail price and your discounted NorthStar Fuel price at each station.
                </Typography>
                <Typography fontSize={14} sx={{ mb: 1.5 }}>
                  <strong>Viewing Savings</strong><br />
                  Check the Savings tab to see:
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li>Total savings over time</li>
                    <li>Savings by fuel type</li>
                    <li>Savings by location</li>
                    <li>Historical savings data</li>
                  </ul>
                </Typography>
                <Typography fontSize={14}>
                  <strong>Maximizing Savings</strong><br />
                  Compare prices across different stations on the map to find the best deals. The app highlights stations with the highest savings potential.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Tips & Best Practices Section - For All Users */}
          <Accordion expanded={expanded === 'tips'} onChange={() => handleAccordionChange('tips')} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize={15}>Tips & Best Practices</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>✓ Enable Location Services</strong><br />
                Always allow location access for accurate station distances and route calculations.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>✓ Check Prices Before You Go</strong><br />
                Compare prices on the map before heading to a station to maximize your savings.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>✓ Use Route Planning</strong><br />
                Plan your routes in advance using the "Plan Route" feature to find fuel stations along your way and optimize your trip.
              </Typography>
              <Typography fontSize={14} sx={{ mb: 1.5 }}>
                <strong>✓ Keep Your App Updated</strong><br />
                Regular updates ensure you have the latest features and accurate fuel price information.
              </Typography>
              <Typography fontSize={14}>
                <strong>✓ Contact Support When Needed</strong><br />
                If you encounter any issues or have questions, use the Support Ticket tab or contact support directly.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default Support; 